import {
  type ActivePlanRes,
  type SubscriptionSettingsRes,
  v1SubscriptionsChangePlanForSaveCreate,
} from '@on3/api/src';
import { Avatar } from '@on3/ui-lib/components/Avatar';
import { useModal } from '@on3/ui-lib/components/Modal/Provider';
import { ServiceLogo } from '@on3/ui-lib/components/ServiceLogo/ServiceLogo';
import { useToast } from '@on3/ui-lib/contexts/ToastProvider';
import { externalApi } from '@on3/ui-lib/utils/api';
import { formatCurrency } from '@on3/ui-lib/utils/recurly';
import { useCallback, useState } from 'react';

import styles from './CancelSave.module.scss';

interface UserCancelSaveModalProps {
  currentPlan: SubscriptionSettingsRes;
  cancelSavePlan: ActivePlanRes;
  handleClose: () => void;
  handleRefreshSubscriptions: () => Promise<void>;
}
const delay = (ms: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });

export const UserCancelSaveModal = ({
  currentPlan,
  cancelSavePlan,
  handleClose,
  handleRefreshSubscriptions,
}: UserCancelSaveModalProps) => {
  const { notify } = useToast();
  const { closeModal } = useModal();
  const [disabled, setDisabled] = useState(false);

  const primaryColor =
    currentPlan.sitePrimaryColor || `var(--site-primary-color)`;
  const savePlanPeriod =
    cancelSavePlan.intervalLength === 12 ? 'year' : 'month';
  const savePlanPrice =
    cancelSavePlan?.rampIntervals?.[0].currencies?.[0].unitAmount;
  const handleDecline = () => {
    setDisabled(true);
    handleClose();
  };
  const handleSubmit = useCallback(async () => {
    if (currentPlan.id === undefined || cancelSavePlan.code === undefined)
      return;
    setDisabled(true);
    try {
      await v1SubscriptionsChangePlanForSaveCreate(
        externalApi,
        currentPlan.id,
        { planCode: cancelSavePlan.code },
      );

      await delay(2000);
      await handleRefreshSubscriptions();
      notify('Your subscription has been updated!', { type: 'success' });
    } catch (error) {
      notify('There was an issue with your subscription change.', {
        type: 'error',
      });
    } finally {
      closeModal();
      setDisabled(false);
    }
  }, [
    cancelSavePlan.code,
    closeModal,
    currentPlan.id,
    handleRefreshSubscriptions,
    notify,
  ]);

  return (
    <div className={styles.block}>
      <div className={styles.logo} style={{ backgroundColor: primaryColor }}>
        <figure>
          {currentPlan.siteAvatar?.source ? (
            <Avatar
              className={styles.avatar}
              domain={currentPlan.siteAvatar?.domain}
              source={currentPlan.siteAvatar?.source}
            />
          ) : (
            <ServiceLogo type="on3" />
          )}
        </figure>
      </div>
      <div className={styles.content}>
        <h1>Upgrade to the subscription that&apos;s right for you.</h1>
        <div>
          <dl>
            <dt style={{ color: primaryColor }}>Your Current Plan</dt>
            <dd>
              <div className={styles.planName}>
                {currentPlan.siteName} {currentPlan.planName}
              </div>
              {currentPlan.subtotal && (
                <div className={styles.priceWrapper}>
                  <span className={styles.price}>
                    {formatCurrency(currentPlan.subtotal)}
                  </span>
                  <span>per year</span>
                </div>
              )}
            </dd>
          </dl>
          <dl>
            <dt style={{ color: primaryColor }}>Your New Plan</dt>
            <dd>
              <div className={styles.planName}>
                {currentPlan.siteName} {cancelSavePlan.name} - Loyalty Pricing
              </div>
              {savePlanPrice && (
                <div className={styles.priceWrapper}>
                  <span className={styles.price}>
                    {formatCurrency(savePlanPrice)}
                  </span>
                  <span>for the next {savePlanPeriod}!</span>
                </div>
              )}
            </dd>
          </dl>
        </div>

        <div className={styles.buttons}>
          <button
            className={styles.btnDecline}
            disabled={disabled}
            onClick={handleDecline}
          >
            No Thanks
          </button>
          <button
            className={styles.btnAccept}
            disabled={disabled}
            onClick={handleSubmit}
          >
            Switch Plan
          </button>
        </div>
      </div>
    </div>
  );
};
